/* eslint-disable */
// ─── Vanity profile URLs ──────────────────────────────────────────────────────
// yogastay.app/<slug> resolves to a verified school or teacher.
//
// The SPA is served for every unmatched path (see vercel.json), so a single
// path segment that isn't a known route is treated as a candidate slug. The
// list below must stay in step with reserved_slugs in the database — the DB is
// authoritative and will refuse a reserved slug outright; this copy only avoids
// a pointless lookup on paths we already own.
import { SUPABASE_URL, SUPABASE_ANON_KEY } from "./security/supabaseClient";
import { friendlyError } from "./userMessage";

export const RESERVED_PATHS = new Set([
  "browse","auth","admin","login","signin","logout","signup","register",
  "dashboard","account","settings","profile","school","schools",
  "teacher","teachers","student","students","class","classes",
  "retreat","retreats","booking","bookings","yoga","styles","blog",
  "static","api","assets","public","images","img","css","js",
  "sitemap","robots","favicon","manifest","index",
  "about","contact","help","support","terms","privacy","legal","respond",
  "pricing","careers","press","security","status","billing",
  "payment","payments","payout","payouts","invoice","refund",
  "verify","verified","official","yogastay","www","mail","email",
  "new","edit","delete","search","explore","home","teach",
  "claim","preview",
  // /teacher-training is a real route (Phase B); without this it was looked up
  // as a school's vanity address and fell back to the home page.
  "teacher-training","training","ytt",
]);

export const SLUG_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;

// Turn a display name into a candidate slug. Mirrors the SQL backfill.
export function slugify(name) {
  return String(name || "")
    .toLowerCase().normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40)
    .replace(/^-+|-+$/g, "");
}

export function validateSlug(slug) {
  const s = String(slug || "").trim().toLowerCase();
  if (!s)                       return "Choose an address";
  if (s.length < 3)             return "At least 3 characters";
  if (s.length > 40)            return "40 characters at most";
  if (!SLUG_RE.test(s))         return "Lowercase letters, numbers and single hyphens only";
  if (RESERVED_PATHS.has(s))    return `“${s}” is reserved — choose another`;
  return null;
}

// Pull the candidate slug out of the current path, or null if it's a real route.
export function slugFromPath(pathname = window.location.pathname) {
  const parts = String(pathname || "").split("/").filter(Boolean);
  if (parts.length !== 1) return null;            // /a/b is never a vanity URL
  const s = parts[0].toLowerCase();
  if (RESERVED_PATHS.has(s)) return null;
  if (!SLUG_RE.test(s))      return null;
  return s;
}

// Two-segment vanity path: /<school-slug>/<retreat-slug>.
//
// Safe alongside the real two-segment routes (/school/x, /blog/x, /yoga/x,
// /styles/x) because every one of those first segments is in RESERVED_PATHS —
// which is also why nothing may ever claim a reserved slug.
export function nestedSlugFromPath(pathname = window.location.pathname) {
  const parts = String(pathname || "").split("/").filter(Boolean);
  if (parts.length !== 2) return null;
  const [a, b] = parts.map(x => x.toLowerCase());
  if (RESERVED_PATHS.has(a))             return null;
  if (!SLUG_RE.test(a) || !SLUG_RE.test(b)) return null;
  return { schoolSlug: a, retreatSlug: b };
}

// Resolve /<school>/<retreat> to { schoolId, retreatId } or null.
export async function resolveNestedSlug({ schoolSlug, retreatSlug }) {
  try {
    const sr = await fetch(
      `${SUPABASE_URL}/rest/v1/schools?slug=eq.${encodeURIComponent(schoolSlug)}&select=id&limit=1`,
      { headers: H });
    const school = (await sr.json())?.[0];
    if (!school?.id) return null;
    const rr = await fetch(
      `${SUPABASE_URL}/rest/v1/retreats?school_id=eq.${school.id}&slug=eq.${encodeURIComponent(retreatSlug)}&is_draft=not.is.true&select=id&limit=1`,
      { headers: H });
    const retreat = (await rr.json())?.[0];
    return { schoolId: school.id, retreatId: retreat?.id || null };
  } catch { return null; }
}

const H = { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` };

// Resolve a slug to { kind:'school'|'teacher', id, name } or null.
// Both tables are queried together: a slug is unique across the two, so at most
// one can answer, and doing them in parallel keeps first paint fast.
export async function resolveSlug(slug) {
  if (!slug) return null;
  try {
    const [sr, tr] = await Promise.all([
      fetch(`${SUPABASE_URL}/rest/v1/schools?slug=eq.${encodeURIComponent(slug)}&is_hidden=not.is.true&select=id,name&limit=1`, { headers: H }),
      fetch(`${SUPABASE_URL}/rest/v1/teachers?slug=eq.${encodeURIComponent(slug)}&select=id,name&limit=1`, { headers: H }),
    ]);
    const [s, t] = await Promise.all([sr.json().catch(() => null), tr.json().catch(() => null)]);
    if (Array.isArray(s) && s[0]) return { kind: "school",  id: s[0].id, name: s[0].name };
    if (Array.isArray(t) && t[0]) return { kind: "teacher", id: t[0].id, name: t[0].name };
  } catch { /* fall through — an unresolvable slug just lands on the home page */ }
  return null;
}

// Is this address free? Checks both tables plus the reserved list.
// The database enforces all of this on write; this is only so the form can say
// so before the user hits save.
export async function isSlugAvailable(slug, { excludeSchoolId, excludeTeacherId } = {}) {
  const err = validateSlug(slug);
  if (err) return { available: false, reason: err };
  try {
    const [sr, tr] = await Promise.all([
      fetch(`${SUPABASE_URL}/rest/v1/schools?slug=eq.${encodeURIComponent(slug)}&select=id&limit=1`, { headers: H }),
      fetch(`${SUPABASE_URL}/rest/v1/teachers?slug=eq.${encodeURIComponent(slug)}&select=id&limit=1`, { headers: H }),
    ]);
    const [s, t] = await Promise.all([sr.json(), tr.json()]);
    const takenBySchool  = Array.isArray(s) && s[0] && s[0].id !== excludeSchoolId;
    const takenByTeacher = Array.isArray(t) && t[0] && t[0].id !== excludeTeacherId;
    if (takenBySchool || takenByTeacher) return { available: false, reason: "Already taken" };
    return { available: true, reason: null };
  } catch {
    // Network trouble is not the same as "taken" — say so rather than
    // blocking a valid address behind a failed check.
    return { available: null, reason: "Could not check right now" };
  }
}

// Save a slug and return a human error message, or null on success.
//
// Done with a direct fetch rather than each dashboard's api.patch because the
// two disagree about failures — SchoolDashboard returns the raw response text,
// TeacherDashboard returns null and drops it. The database's own wording is the
// useful part here ("reserved", "already taken", "only once verified"), so it
// is worth keeping intact.
export async function saveSlug(table, id, slug, token) {
  try {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}?id=eq.${id}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${token || SUPABASE_ANON_KEY}`,
        Prefer: "return=representation",
      },
      body: JSON.stringify({ slug }),
    });
    if (res.ok) return null;

    const text = await res.text();
    // Never surface the database's own wording — it names constraints and
    // columns. friendlyError() recognises our trigger cases (reserved, taken,
    // not verified) and returns app-owned copy, logging the rest.
    if (/slug_format_check/i.test(text)) {
      console.error("[slug save]", text);
      return "Use lowercase letters, numbers and single hyphens only.";
    }
    return friendlyError({ status: res.status, message: text }, { context: "slug save" });
  } catch (e) {
    console.error("[slug save] network", e);
    return "Network problem — please check your connection and try again.";
  }
}
